import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  SelectChangeEvent,
} from "@mui/material";
import {
  Add,
  Edit,
  Refresh,
  Search,
  Clear,
} from "@mui/icons-material";
import { callApi } from "../../../utils/apiHelper";
import { apiEndpoints } from "../../../utils/apiEndpoints";
import {
  RawMaterial,
  UnitConversion,
  UnitType,
  unitCategoryMap
} from "../../../utils/commonApis";
import UnitConverter from "../../UnitConverter";

// Raw material types
const materialTypes = [
  { value: "auto allot", label: "Auto Allot" },
  { value: "Fixed asset", label: "Fixed Asset" },
  { value: "admin controlled allot", label: "Admin Controlled Allot" },
];

const RawMaterialManagement: React.FC = () => {
  // State variables
    const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
    const [filteredMaterials, setFilteredMaterials] = useState<RawMaterial[]>([]);
    const [loading, setLoading] = useState(true);
    const [openMaterialForm, setOpenMaterialForm] = useState(false);
    const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
    const [currentMaterial, setCurrentMaterial] = useState<RawMaterial | null>(null);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [visibilityFilter, setVisibilityFilter] = useState<string>("all");
    const [alert, setAlert] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info" | "warning";
    }>({
      open: false,
      message: "",
      severity: "info",
    });

  // Form fields
  const [formData, setFormData] = useState<{
    Name: string;
    RawMaterialType: string;
    BaseUnit: string;
    UnitConversions: UnitConversion[];
    Price: number;
    Description: string;
    Visibility: boolean;
  }>({
    Name: "",
    RawMaterialType: "auto allot",
    BaseUnit: "Kg",
    UnitConversions: [],
    Price: 0,
    Description: "",
    Visibility: true,
  });

  // Fetch all raw materials when component mounts
  useEffect(() => {
    fetchRawMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to fetch all raw materials from API
  const fetchRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await callApi<{ raw_materials: RawMaterial[] }>(
        apiEndpoints.NulookRawMaterialManagement,
        "POST",
        {
          operation: "get_all",
        }
      );

      if (response.data?.raw_materials) {
        setRawMaterials(response.data.raw_materials);
        setFilteredMaterials(response.data.raw_materials);
      } else {
        showAlert("Failed to load raw materials", "error");
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
      showAlert("Error loading raw materials", "error");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Function to handle select changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Function to handle visibility toggle
  const handleVisibilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      Visibility: e.target.checked,
    });
  };

  // Function to handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Function to open form for creating a new material
  const handleAddMaterial = () => {
    setFormMode("create");
    setFormData({
      Name: "",
      RawMaterialType: "auto allot",
      BaseUnit: "Kg",
      UnitConversions: [],
      Price: 0,
      Description: "",
      Visibility: true,
    });
    setOpenMaterialForm(true);
  };

  // Function to open form for editing a material
  const handleEditMaterial = (material: RawMaterial) => {
    setFormMode("edit");
    setCurrentMaterial(material);
    setFormData({
      Name: material.Name,
      RawMaterialType: material.RawMaterialType || "auto allot",
      BaseUnit: material.BaseUnit || "Kg",
      UnitConversions: material.UnitConversions || [],
      Price: material.Price || 0,
      Description: material.Description || "",
      Visibility: material.Visibility !== undefined ? material.Visibility : true,
    });
    setOpenMaterialForm(true);
  };

  // Function to view material details
  const handleViewDetails = (material: RawMaterial) => {
    setCurrentMaterial(material);
    setOpenDetailsDialog(true);
  };

  // Function to toggle material visibility
  const handleToggleVisibility = async (material: RawMaterial) => {
    try {
      const response = await callApi(
        apiEndpoints.NulookRawMaterialManagement,
        "POST",
        {
          operation: "update",
          RawMaterialId: material.RawMaterialId,
          Visibility: !material.Visibility,
        }
      );

      if (response.data) {
        showAlert(
          `Material ${material.Visibility ? "hidden" : "visible"} successfully`,
          "success"
        );
        fetchRawMaterials();
      } else {
        showAlert("Failed to update material visibility", "error");
      }
    } catch (error) {
      console.error("Error toggling material visibility:", error);
      showAlert("Error updating material visibility", "error");
    }
  };

  // Function to submit material form (create or edit)
  const handleSubmitMaterialForm = async () => {
    // Form validation
    if (
      formData.Name.trim() === "" ||
      formData.RawMaterialType.trim() === "" ||
      formData.BaseUnit.trim() === ""
    ) {
      showAlert("Please fill all required fields", "warning");
      return;
    }

    try {
      if (formMode === "create") {
        // Generate a code based on the name and a random UUID
        const uuid = crypto.randomUUID ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).substring(2, 10);
        const code = `${formData.Name.trim().replace(/\s+/g, '-').toLowerCase()}-${uuid}`;
        
        const response = await callApi(
          apiEndpoints.NulookRawMaterialManagement,
          "POST",
          {
            operation: "create",
            ...formData,
            Code: code,
            Price: Number(formData.Price),
          }
        );

        if (response.data) {
          showAlert("Raw material created successfully", "success");
          setOpenMaterialForm(false);
          fetchRawMaterials();
        } else {
          showAlert(
            response.error?.message || "Failed to create raw material",
            "error"
          );
        }
      } else if (formMode === "edit" && currentMaterial) {
        const response = await callApi(
          apiEndpoints.NulookRawMaterialManagement,
          "POST",
          {
            operation: "update",
            RawMaterialId: currentMaterial.RawMaterialId,
            ...formData,
            Code: currentMaterial.Code || `${formData.Name.trim().replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).substring(2, 10)}`, // Use existing code or generate new one if missing
            Price: Number(formData.Price),
          }
        );

        if (response.data) {
          showAlert("Raw material updated successfully", "success");
          setOpenMaterialForm(false);
          fetchRawMaterials();
        } else {
          showAlert(
            response.error?.message || "Failed to update raw material",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showAlert("Error processing raw material", "error");
    }
  };

  // Function to handle adding a unit conversion
  const handleAddUnitConversion = (conversion: UnitConversion) => {
    setFormData({
      ...formData,
      UnitConversions: [...formData.UnitConversions, conversion],
    });
  };

  // Function to show alerts
  const showAlert = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Function to close alert
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    });
  };

  // Apply filters to materials
  useEffect(() => {
    let result = [...rawMaterials];
    
    // Apply search term filter
      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        result = result.filter(
          material => 
            material.Name.toLowerCase().includes(lowerSearchTerm) ||
            (material.Description?.toLowerCase() || '').includes(lowerSearchTerm) ||
            (material.Code?.toLowerCase() || '').includes(lowerSearchTerm)
        );
      }

    // Apply type filter
    if (typeFilter !== "all") {
      result = result.filter(material => material.RawMaterialType === typeFilter);
    }

    // Apply visibility filter
    if (visibilityFilter !== "all") {
      const isVisible = visibilityFilter === "visible";
      result = result.filter(material => material.Visibility === isVisible);
    }

    setFilteredMaterials(result);
  }, [searchTerm, typeFilter, visibilityFilter, rawMaterials]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Alert for notifications */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Raw Material Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchRawMaterials}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddMaterial}
          >
            Add Raw Material
          </Button>
        </Box>
      </Box>

      {/* Search and filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              placeholder="Search by name, code, or description..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">Material Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={typeFilter}
                label="Material Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                {materialTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="visibility-filter-label">Visibility</InputLabel>
              <Select
                labelId="visibility-filter-label"
                value={visibilityFilter}
                label="Visibility"
                onChange={(e) => setVisibilityFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="visible">Visible</MenuItem>
                <MenuItem value="hidden">Hidden</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Base Unit</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Visibility</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress sx={{ my: 3 }} />
                </TableCell>
              </TableRow>
            ) : filteredMaterials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    No raw materials found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMaterials.map((material) => (
                <TableRow key={material.RawMaterialId}>
                  <TableCell>{material.Name}</TableCell>
                  <TableCell>
                    <Chip
                      label={material.RawMaterialType || "Unknown"}
                      color="default"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{material.BaseUnit}</TableCell>
                  <TableCell>₹{material.Price?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={material.Visibility ? "Visible" : "Hidden"}
                      arrow
                    >
                      <Switch
                        checked={material.Visibility}
                        onChange={() => handleToggleVisibility(material)}
                        color="primary"
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        color="info"
                        size="small"
                        onClick={() => handleViewDetails(material)}
                        sx={{ mr: 1 }}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEditMaterial(material)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Raw Material Form Dialog */}
      <Dialog
        open={openMaterialForm}
        onClose={() => setOpenMaterialForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formMode === "create" ? "Add New Raw Material" : `Edit Raw Material: ${currentMaterial?.Name || ''}`}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  name="Name"
                  value={formData.Name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="material-type-label">Material Type</InputLabel>
                  <Select
                    labelId="material-type-label"
                    name="RawMaterialType"
                    value={formData.RawMaterialType}
                    label="Material Type"
                    onChange={handleSelectChange}
                  >
                    {materialTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="base-unit-label">Base Unit</InputLabel>
                  <Select
                    labelId="base-unit-label"
                    name="BaseUnit"
                    value={formData.BaseUnit}
                    label="Base Unit"
                    onChange={handleSelectChange}
                  >
                    {Object.keys(unitCategoryMap).map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Price"
                  name="Price"
                  type="number"
                  value={formData.Price}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Visibility}
                      onChange={handleVisibilityChange}
                      name="Visibility"
                      color="primary"
                    />
                  }
                  label="Visible"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="Description"
                  value={formData.Description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6">Unit Conversions</Typography>
            <UnitConverter
              mode="add"
              baseUnit={formData.BaseUnit as UnitType}
              baseValue={1}
              unitConversions={formData.UnitConversions}
              onAddConversion={handleAddUnitConversion}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMaterialForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitMaterialForm}
            disabled={
              formData.Name.trim() === "" ||
              formData.RawMaterialType.trim() === "" ||
              formData.BaseUnit.trim() === ""
            }
          >
            {formMode === "create" ? "Create" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Raw Material Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenDetailsDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Clear />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {currentMaterial && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {currentMaterial.Name}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Code (Internal ID)
                  </Typography>
                  <Typography variant="body1">
                    {currentMaterial.Code || "Not Available"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Material Type
                  </Typography>
                  <Typography variant="body1">
                    {currentMaterial.RawMaterialType || "Not Specified"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Base Unit
                  </Typography>
                  <Typography variant="body1">
                    {currentMaterial.BaseUnit || "Not Specified"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Price
                  </Typography>
                  <Typography variant="body1">
                    ₹{currentMaterial.Price?.toFixed(2) || "0.00"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Visibility
                  </Typography>
                  <Typography variant="body1">
                    {currentMaterial.Visibility ? "Visible" : "Hidden"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {currentMaterial.CreatedAt 
                      ? new Date(currentMaterial.CreatedAt * 1000).toLocaleString() 
                      : "Not Available"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {currentMaterial.Description || "No description available"}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Unit Conversions
                </Typography>
                {currentMaterial.UnitConversions && currentMaterial.UnitConversions.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Unit</TableCell>
                          <TableCell>Conversion Factor</TableCell>
                          <TableCell>Equivalent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentMaterial.UnitConversions.map((conversion, index) => (
                          <TableRow key={index}>
                            <TableCell>{conversion.unit}</TableCell>
                            <TableCell>{conversion.conversionFactor}</TableCell>
                            <TableCell>
                              1 {currentMaterial.BaseUnit} = {conversion.conversionFactor} {conversion.unit}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No unit conversions defined
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            onClick={() => {
              setOpenDetailsDialog(false);
              if (currentMaterial) {
                handleEditMaterial(currentMaterial);
              }
            }}
          >
            Edit
          </Button>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RawMaterialManagement;